import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Button from '../../common/components/button';
import Div from '../../common/components/div';
import H2 from '../../common/components/h2';
import H3 from '../../common/components/h3';
import StackingButtons from '../../common/components/StackingButtons';
import ValidationAlertMessage from '../../common/components/ValidationAlertMessage';
import translate from '../../common/functions/translate';
import { useConversion } from '../../common/hooks/use.analytics';
import useTrait from '../../common/hooks/useTrait';
import is from '../../common/objects/is';
import ErrorAlert from '../../common/components/ErrorAlert';
import { hasInvalidField } from '../../common/functions/getApiErrorMessage';
import { SecretQuestionDropdown } from '../../common/components/SecretQuestionDropdown';
import { NewPassword } from '../../common/components/NewPassword/NewPassword';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import { NewUsername } from '../../common/components/NewUsername/NewUsername';
import useCreateAccountApi from '../../common/hooks/useCreateAccountApi';
import { globalData } from '../../globalContext';
import { createOrUnlockAccountData } from '../createAccountContext';
import { scrollIntoViewAndFocus } from '../../common/functions/scrollIntoViewAndFocus';
import { TextField } from '@cmsgov/ds-medicare-gov';
import getUrlParameters from '../../common/functions/getUrlParameters';
import { HorizontalRule } from '../../common/components/HorizontalRule';

export type AccountDetailsErrorKeys = 'processInconvenience' | 'getHelp' | 'processReenter';
export type AccountDetailsHeaderKeys = 'completeItems' | 'alreadyExists' | 'cantProcess' | 'alreadyHaveAccount';
type AccountDetailsListKeys = 'pleaseEnterUsername' | 'passwordGuidelines' | 'secretQuestion' | 'secretAnswer' | 'usernameAlreadyExists';

interface YourAccountDetailsProps {
    testInitialErrorState?: AccountDetailsErrorKeys & AccountDetailsListKeys
    testHeaderKey?: AccountDetailsHeaderKeys
}

const YourAccountDetails = (props: YourAccountDetailsProps) => {
    const [confirmPasswordRef, setConfirmPasswordRef] = useState<HTMLElement | null>(null);
    const [passwordRef, setPasswordRef] = useState<HTMLElement | null>(null);
    const [password, setPassword] = useState('');
    const [hasPasswordError, setHasPasswordError] = useState(false);
    const [hasConfirmPasswordError, setHasConfirmPasswordError] = useState(false);
    const [secretAnswerRef, setSecretAnswerRef] = useState<HTMLElement | null>(null);
    const [secretQuestionRef, setSecretQuestionRef] = useState<HTMLElement | null>(null);
    const [usernameRef, setUsernameRef] = useState<HTMLElement | null>(null);
    const confirmPasswordError = useTrait(false);
    const passwordError = useTrait(false);
    const secretAnswerError = useTrait(false);
    const secretQuestionError = useTrait(false);
    const usernameError = useTrait(false);
    const validateEntireForm = useTrait(false);
    const [isLoading, setIsLoading] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [username, setUsername] = useState('');
    const [hasUsernameError, setHasUsernameError] = useState(false);
    const params = getUrlParameters();

    const conversion = useConversion('sign_up', 'sign_up');
    const history = useHistory();

    const [errorMessage, setErrorMessage] = useState<JSX.Element | string | null>("");
    const [errorKey, setErrorKey] = useState<AccountDetailsErrorKeys | null>(props.testInitialErrorState || null);
    const [errorKeyHeader, setErrorKeyHeader] = useState<AccountDetailsHeaderKeys | null>(props.testHeaderKey || null);
    const [errorAlertHeading, setErrorAlertHeading] = useState("");
    const [errorListKey, setErrorListKey] = useState<AccountDetailsListKeys | null>(props.testInitialErrorState || null);
    const [apiErrorMessageList, setApiErrorMessageList] = useState<any>([]);

    const [analyticsErrorKey, setAnalyticsErrorKey] = useState<AccountDetailsErrorKeys | AccountDetailsListKeys | null>(null);
    const currentListKey = useRef<string | null>('');
    const errorContainerRef = useRef<HTMLDivElement | null>(null);
    const header = translate('set_up_account');

    useEffect(() => {
        const errorHeaders: {
            [key in AccountDetailsHeaderKeys]: string;
        } = {
            completeItems: translate("complete_items_to_create_account"),
            alreadyExists: translate("looks_like_already_have_account"),
            alreadyHaveAccount: translate('looks_like_already_have_account'),
            cantProcess: translate("we_cant_process_request")
        };

        setErrorAlertHeading(errorKeyHeader ? errorHeaders[errorKeyHeader] : "");
    }, [setErrorAlertHeading, errorKeyHeader]);

    useEffect(() => {
        const errorKeyToMessage: {
          [key in AccountDetailsErrorKeys]: string | JSX.Element
        } = {
            processReenter: translate("we_cant_process_request_reenter_and_try_again"),
            processInconvenience: translate("we_are_sorry_for_inconvenience_try_creating_account_later"),
            getHelp: <>
                <div>
                    <Link to="/forgot-username-and-password/">
                        {translate('get_help')}
                    </Link> {' '}
                    {translate('do_not_remember_your_username_and_password')}
                </div>
            </>
        }
        setErrorMessage(errorKey ? errorKeyToMessage[errorKey] : null);
    }, [errorKey, apiErrorMessageList, passwordRef, usernameRef, secretQuestionRef, secretAnswerRef]);

    useEffect(() => {
        const errorKeyToMessage: {
          [key in AccountDetailsListKeys]: string | JSX.Element
        } = {
            pleaseEnterUsername: <>
                <ValidationAlertMessage
                    description={translate("create_username_for_your_account")}
                    fieldReference={usernameRef}
                    linkText={translate("username")}
                    componentHeading={header}
                />
            </>,
            usernameAlreadyExists: <>
                <ValidationAlertMessage
                    description={translate("this_username_already_used")}
                    fieldReference={usernameRef}
                    linkText={translate("username")}
                    componentHeading={header}
                />
            </>,
            passwordGuidelines: <>
                <ValidationAlertMessage
                    description={translate("create_password_for_your_account")}
                    fieldReference={passwordRef}
                    linkText={translate("password")}
                    componentHeading={header}
                />
            </>,
            secretQuestion: <>
                <ValidationAlertMessage
                    description={translate("pick_secret_question")}
                    fieldReference={secretQuestionRef}
                    linkText={translate("secret_question")}
                    componentHeading={header}
                />
            </>,
            secretAnswer: <>
                <ValidationAlertMessage
                    description={translate("enter_answer_to_secret_questions")}
                    fieldReference={secretAnswerRef}
                    linkText={translate("secret_answer")}
                    componentHeading={header}
                />
            </>
        }
        if (currentListKey.current === errorListKey) {
            return;
        }
        if (errorListKey && errorListKey in errorKeyToMessage) {
            setApiErrorMessageList([errorKeyToMessage[errorListKey], ...apiErrorMessageList]);
        } else {
            setApiErrorMessageList([]);
        }
        currentListKey.current = errorListKey;
    }, [errorListKey, apiErrorMessageList, passwordRef, usernameRef, secretQuestionRef, secretAnswerRef, header]);


    useEffect(() => {
        const errorStrings: {
            [key in AccountDetailsErrorKeys | AccountDetailsListKeys]: string;
        } = {
            processReenter: "We couldn't process your request. Please re-enter your information and try again.",
            processInconvenience: "We're sorry for any inconvenience. Try creating your account later.",
            getHelp: "Get help if you don't remember your username or password.",
            pleaseEnterUsername: "Create a username for your account using the guidelines listed.",
            usernameAlreadyExists: "This username is already being used by someone else. Try creating another username, using the guidelines listed.",
            passwordGuidelines: "Create a password for your account using the guidelines listed.",
            secretQuestion: "Pick a secret question.",
            secretAnswer: "Enter an answer to your secret question."
        };
        if (analyticsErrorKey) {
            conversion.failAPIIntermediate(errorStrings[analyticsErrorKey]);
            conversion.failAPI(errorStrings[analyticsErrorKey]);
        }
    }, [analyticsErrorKey, conversion]);

    const api = useCreateAccountApi();
    const { data, updateData, stepBackward } = useContext(createOrUnlockAccountData);
    const { secretQuestion, secretAnswer, analyticsPart } = data;

    const { setShowNewAccountLoginAlert } = useContext(globalData);

    const callCreateAccount = async () => {
        let promptForMfa: boolean | undefined;
        if(params.has('prompt_for_mfa')) {
            promptForMfa = params.get('prompt_for_mfa') !== '0';
        }
        const response = await api.step3(username, password, secretQuestion, secretAnswer, promptForMfa);
        setIsLoading(false);
        const statusCode = response?.status;
        const errors = response?.data?.errors;
        validateEntireForm.set(false);
        if(statusCode !== 204){
            scrollIntoViewAndFocus(errorContainerRef.current);
        }
        switch (statusCode) {
            case 204:
                conversion.success(analyticsPart);
                setShowNewAccountLoginAlert(true);
                history.push('/login');
                break;
            case 400:
                const invalidUsername = hasInvalidField(errors, 'username') || hasInvalidField(errors, 'updates.username');
                const invalidPassword = hasInvalidField(errors, 'password') || hasInvalidField(errors, 'updates.password');
                const invalidQuestionsAndAnswers = hasInvalidField(response?.data.errors, 'challengeQuestionAndAnswers');
                if (invalidUsername) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('pleaseEnterUsername');
                    setAnalyticsErrorKey('pleaseEnterUsername');
                } else if (invalidPassword) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('passwordGuidelines');
                    setAnalyticsErrorKey('passwordGuidelines');
                }
                else if (invalidQuestionsAndAnswers) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('secretQuestion');
                    setErrorListKey('secretAnswer');
                    setAnalyticsErrorKey('secretQuestion');
                    setAnalyticsErrorKey('secretAnswer');
                }
                else {
                    setErrorKeyHeader('completeItems');
                    setErrorKey('processReenter');
                    setAnalyticsErrorKey('processReenter');
                }
                break;
            case 409:
                setErrorKeyHeader('completeItems');
                setErrorListKey('usernameAlreadyExists');
                setAnalyticsErrorKey('usernameAlreadyExists');
                break;
            default:
                setErrorKeyHeader('cantProcess');
                setErrorKey('processInconvenience');
                setAnalyticsErrorKey('processInconvenience');
                break;
        }
    };

    const getApiErrorMessage = () => {
        if (!errorMessage)
            return null;
        return errorMessage;
    };

    const getBackButton = () => {
        return <>
            <Button
                onClick={stepBackward}
            >
                {translate('back')}
            </Button>
        </>;
    };

    const getCreateAccountButton = () => {
        return <>
            <Button
                variation={'solid'}
                type={'submit'}
                isLoading={isLoading}
            >
                {translate('create_account')}
            </Button>
        </>;
    };

    const getValidationAlertMessages = () => {
        const messages: JSX.Element[] = [];
        if (!validateEntireForm.get())
            return messages;
        if (hasUsernameError)
            messages.push(
                <ValidationAlertMessage
                    description={translate('create_username_for_your_account')}
                    fieldReference={usernameRef}
                    linkText={translate('username')}
                    componentHeading={header}
                />
            );
        if (hasPasswordError)
            messages.push(
                <ValidationAlertMessage
                    description={translate('create_password_for_your_account')}
                    fieldReference={passwordRef}
                    linkText={translate('password')}
                    componentHeading={header}
                />
            );
        if (hasConfirmPasswordError)
            messages.push(
                <ValidationAlertMessage
                    description={translate('reenter_new_password')}
                    fieldReference={confirmPasswordRef}
                    linkText={translate('confirm_password')}
                    componentHeading={header}
                />
            );
        if (secretQuestion === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('pick_secret_question')}
                    fieldReference={secretQuestionRef}
                    linkText={translate('secret_question')}
                    componentHeading={header}
                />
            );
        if (secretAnswer === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_answer_to_secret_questions')}
                    fieldReference={secretAnswerRef}
                    linkText={translate('secret_answer')}
                    componentHeading={header}
                />
            );
        return messages;
    };

    const handleCreateAccountButtonClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        resetErrors();
        setSubmitAttempted(true);
        if (isLoading) {
            return;
        }
        setSubmitAttempted(true);
        setIsLoading(true);
        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef != null) {
            scrollIntoViewAndFocus(errorFieldRef);
            setIsLoading(false);
            return;
        }
        callCreateAccount();
    };

    const resetErrors = () => {
        setErrorKeyHeader(null);
        setErrorKey(null);
        setErrorListKey(null);
        confirmPasswordError.set(false);
        passwordError.set(false);
        secretAnswerError.set(false);
        secretQuestionError.set(false);
        usernameError.set(false);
    };

    const updatePassword = (value: string) => {
        setPassword(value);
    };

    const updateSecretAnswer = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ secretAnswer: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateSecretAnswer();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateSecretQuestion = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ secretQuestion: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateSecretQuestion();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateUsername = (value: string) => {
        setUsername(value);
    };

    const validateForm = (): HTMLElement | null | undefined => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (hasUsernameError)
            errorFieldRef = usernameRef;
        if (hasPasswordError && !errorFieldRef)
            errorFieldRef = passwordRef;
        if (hasConfirmPasswordError && !errorFieldRef)
            errorFieldRef = confirmPasswordRef;
        if (!validateSecretQuestion() && !errorFieldRef)
            errorFieldRef = secretQuestionRef;
        if (!validateSecretAnswer() && !errorFieldRef)
            errorFieldRef = secretAnswerRef;
        const messages = getValidationAlertMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidation(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };

    const validateSecretAnswer = () => {
        const isValid = secretAnswer !== '';
        if (secretAnswerError.get() === isValid)
            secretAnswerError.set(!isValid);
        return isValid;
    };

    const validateSecretQuestion = () => {
        const isValid = secretQuestion !== '';
        if (secretQuestionError.get() === isValid)
            secretQuestionError.set(!isValid);
        return isValid;
    };

    function getApiErrorKeyPassword() {
        if (errorListKey === 'passwordGuidelines') {
            return errorListKey;
        }
    }

    function getApiErrorKeyUsername() {
        if (errorListKey === 'pleaseEnterUsername') {
            return errorListKey;
        }
    }

    function getQuestionContext() {
        if (secretQuestionError.get() || errorListKey === 'secretQuestion') {
            return 'createAccount';
        }
        return null;
    }

    function getSecretAnswerErrorMessage() {
        if (secretAnswerError.get()) {
            return translate('enter_answer_to_secret_questions');
        } else if (errorListKey === 'secretAnswer') {
            return translate('check_answer_to_secret_question');
        } else {
            return null;
        }
    }

    return <>
        <form onSubmit={handleCreateAccountButtonClick} method="POST">
            <fieldset style={{
                border: 0,
                margin: 0,
                padding: 0,
            }}>
                <Div isRow>
                    <Div cols={12}>
                        <legend>
                            <H2 className={'ds-u-margin-top--1 ds-u-margin-bottom--3'}>
                                {translate('set_up_account')}
                            </H2>
                        </legend>
                        <AllFieldsRequired />
                        <div ref={errorContainerRef} tabIndex={-1}>
                            {(getApiErrorMessage() || apiErrorMessageList.length > 0) && getValidationAlertMessages().length === 0 &&
                                <ErrorAlert
                                    heading={errorAlertHeading}
                                    errorMessage={getApiErrorMessage()}
                                    validationMessages={apiErrorMessageList}/>}
                            {getValidationAlertMessages().length !== 0 &&
                                <ErrorAlert
                                    headingId={'validationAlertHeader'}
                                    heading={translate('complete_items_to_create_account')}
                                    validationMessages={getValidationAlertMessages()} />}
                        </div>
                        <H3>
                            {translate('create_username')}
                        </H3>
                    </Div>
                </Div>

                <Div isRow>
                    <Div cols={12} colsMD={10}>
                        <NewUsername
                            component={'yourAccountDetails'}
                            submitAttempted={submitAttempted}
                            inputRef={setUsernameRef}
                            onUsernameError={setHasUsernameError}
                            onUsernameChange={updateUsername}
                            apiErrorKey={getApiErrorKeyUsername()}
                        />
                    </Div>
                </Div>
                <HorizontalRule />
                <Div isRow>
                    <Div cols={12} colsMD={10}>
                        <H3 className="ds-u-margin-top--3">
                            {translate('create_password')}
                        </H3>
                    </Div>
                </Div>
                <Div isRow>
                    <Div cols={12} colsMD={10}>
                        <NewPassword
                            component={'yourAccountDetails'}
                            submitAttempted={submitAttempted}
                            passwordRef={setPasswordRef}
                            confirmPasswordRef={setConfirmPasswordRef}
                            onPasswordChange={updatePassword}
                            onPasswordError={setHasPasswordError}
                            onConfirmPasswordError={setHasConfirmPasswordError}
                            apiErrorKey={getApiErrorKeyPassword()}
                        />
                    </Div>
                </Div>
                <HorizontalRule marginBottom={4} />
                <Div isRow>
                    <Div cols={12}>
                        <H3>{translate('secure_your_account')}</H3>
                    </Div>
                </Div>

                <Div isRow>
                    <Div cols={12} colsMD={10}>
                        <SecretQuestionDropdown
                            inputRef={setSecretQuestionRef}
                            onBlur={event => updateSecretQuestion(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateSecretQuestion(event.currentTarget.value)}
                            value={secretQuestion}
                            context={getQuestionContext()}
                        />
                        <TextField
                            errorMessage={getSecretAnswerErrorMessage()}
                            inputRef={setSecretAnswerRef}
                            label={translate('secret_answer')}
                            name={'secretAnswer'}
                            onBlur={event => updateSecretAnswer(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateSecretAnswer(event.currentTarget.value)}
                            value={secretAnswer}
                        />
                    </Div>
                </Div>
                <StackingButtons
                    button1={getBackButton()}
                    button2={getCreateAccountButton()}
                />
            </fieldset>
        </form>
    </>;
}

export default YourAccountDetails;
